import { GetServerSideProps, NextPage } from 'next'
import nextI18nextConfig from '~/next-i18next.config'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { DependencyInjector } from '~/injector/dependency.injector'
import { HtmlPageMetaContextService } from '~/components/HtmlPageMeta/HtmlPageMetaContextService'
import { ProfileRepositoryInterface } from '~/modules/Profile/ProfileRepositoryInterface'
import { TrophyRepositoryInterface } from '~/modules/Trophies/Domain/TrophyRepositoryInterface'
import { ApplicationProductSorter } from '~/modules/Products/Application/ApplicationProductSorter'
import { GetUserGallery } from '~/modules/UserGallery/GetUserGallery'
import { GetUser } from '~/modules/Auth/Application/GetUser/GetUser'
import { UserRepositoryInterface } from '~/modules/Auth/Domain/UserRepositoryInterface'
import { GetUserApplicationException } from '~/modules/Auth/Application/GetUser/GetUserApplicationException'
import { UserGalleryException } from '~/modules/UserGallery/UserGalleryException'
import { GetProfile } from '~/modules/Profile/GetProfile'
import { MarketUserGalleryPage } from '~/components/MarketPages/UserGalleryPage'
import {
  fromUserGalleryPagePropsToViewModel,
  UserGalleryPageRouteProps
} from '~/modules/PresentationAdapters/UserGalleryPageAdapter'
import { ComponentTrophyDtoTransformer } from '~/modules/Products/Infrastructure/ComponentTrophyDtoTransformer'

export const getServerSideProps: GetServerSideProps = async (context) => {
  const locale = context.locale ? context.locale : nextI18nextConfig.i18n.defaultLocale
  const slug = context.query.slug as string
  const translationsPromise = serverSideTranslations(locale, [
    'user_tos',
    'app_menu',
    'footer',
    'wallet_error_modal',
    'user_gallery',
    'trophies',
  ])
  const htmlPageMetaContextService = new HtmlPageMetaContextService(context)

  const di = DependencyInjector.getInstance()
  const userRepository: UserRepositoryInterface = di.resolve('UserRepositoryInterface')
  const profileRepository: ProfileRepositoryInterface = di.resolve('ProfileRepositoryInterface')
  const trophyRepository: TrophyRepositoryInterface = di.resolve('TrophyRepositoryInterface')
  const sorterService = new ApplicationProductSorter()

  const getUserGallery = new GetUserGallery(profileRepository, trophyRepository, sorterService)
  const getProfile = new GetProfile(profileRepository)
  const getUser = new GetUser(userRepository)

  try {
    const [products, user] = await Promise.all([
      getUserGallery.getBySlug(slug),
      getUser.getUserPublicBySlug(slug),
    ])
    const profile = await getProfile.get(user.id)
    const componentProducts = products.map((product) => ComponentTrophyDtoTransformer.fromApplication(product))

    const props: UserGalleryPageRouteProps = {
      user,
      profile,
      products: JSON.parse(JSON.stringify(componentProducts)),
      htmlPageMetaContextProps: htmlPageMetaContextService.getProperties(),
      ...await translationsPromise,
    }

    return { props }
  } catch (exception: unknown) {
    const isNotFound = (
      exception instanceof GetUserApplicationException &&
      exception.id === GetUserApplicationException.userNotFoundId
    ) || (
      exception instanceof UserGalleryException &&
      exception.id === UserGalleryException.notFoundId
    )

    if (isNotFound) {
      return { notFound: true }
    }

    throw exception
  }
}

const GallerySlugPage: NextPage<UserGalleryPageRouteProps> = (props) => {
  return <MarketUserGalleryPage viewModel={ fromUserGalleryPagePropsToViewModel(props) } />
}

export default GallerySlugPage
